
import { computed, defineComponent, ref, Ref, toRef, watch } from 'vue';
import { useField } from 'vee-validate';
import InputShowPassword from '@/shared/form/InputShowPassword.vue';
import { getMaybeRequiredLabel, useValidationSchema } from '@/shared/form/useRequiredForm';

export default defineComponent({
    name: 'InputText',
    components: { InputShowPassword },
    inheritAttrs: false,
    props: {
        type: {
            type: String,
            default: 'text',
        },
        value: {
            type: [String, Number],
            default: '',
        },
        // eslint-disable-next-line vue/require-default-prop
        modelValue: {
            type: null,
        },
        name: {
            type: String,
            required: true,
        },
        label: {
            type: String,
            default: '',
        },
        id: {
            type: String,
            required: false,
            default: null,
        },
        autofocus: {
            type: Boolean,
            required: false,
            default: false,
        },
        showErrors: {
            type: Boolean,
            required: false,
            default: true,
        },
        allowPasswordToggle: {
            type: Boolean,
            required: false,
            default: true,
        },
        inputClear: {
            type: Boolean,
            required: false,
            default: true,
        },
        inputCssClass: {
            type: String,
            default: '',
        },
        rules: {
            type: String,
            default: '',
        },
    },
    emits: {
        clear: () => true,
        input: () => true,
        'update:modelValue': (value) => true,
    },
    setup(props, { attrs, emit }) {
        const isFocused: Ref<boolean> = ref(false);
        const showPassword: Ref<boolean> = ref(false);

        const isModelBound = computed(() => {
            return 'modelValue' in props;
        });

        const fieldRules = computed(() => props.rules);

        const {
            value: inputValue,
            errorMessage,
            handleChange,
            handleBlur,
            validate,
            resetField,
            meta,
        } = useField(props.name, fieldRules.value, {
            initialValue: isModelBound.value ? props.modelValue : props.value,
            validateOnValueUpdate: false,
        });

        const schema = useValidationSchema();
        const maybeRequiredLabel = computed<string>(() => getMaybeRequiredLabel(props.name, schema, props.label));

        const fieldId = computed(() => {
            return props.id || props.name + Math.random();
        });

        const isSearchType = computed(() => {
            return props.type === 'search';
        });

        const fieldAttrs = computed(() => {
            const fieldAttrs = {
                ...attrs,
                class: '',
            };
            return fieldAttrs;
        });

        const showInputClear = computed(() => {
            return isFocused.value && !!inputValue.value;
        });

        const inputType = computed(() => {
            return showPassword.value ? 'text' : props.type;
        });

        const passwordToggleMode = computed(() => {
            return props.allowPasswordToggle && props.type === 'password';
        });

        const handleClear = () => {
            resetField({ value: '', touched: false, errors: [] });
            isModelBound.value && emit('update:modelValue', inputValue.value);
            emit('clear');
        };

        const handleInputEvent = (evt) => {
            isModelBound.value && emit('update:modelValue', evt.target.value);
            emit('input', evt);
            handleChange(evt, false);
        };

        const handleChangeEvent = (evt?) => {
            if (evt) {
                isModelBound.value && emit('update:modelValue', evt.target.value);
                emit('input', evt);
                handleChange(evt);
            }
        };

        const validationListeners = computed(() => {
            // if no current errors use lazy validation handlers
            const listeners = {
                blur: handleBlur,
                change: handleChangeEvent,
                input: handleInputEvent,
            };

            // if current errors replace with eager handlers to validate changes immediately
            if (errorMessage.value) {
                listeners.blur = handleChangeEvent;
                listeners.input = handleChangeEvent;
            }

            return listeners;
        });

        if (isModelBound.value) {
            const modelValue = toRef(props, 'modelValue');
            watch(modelValue, newModelValue => {
                if (newModelValue !== inputValue.value) {
                    inputValue.value = newModelValue;
                    validate();
                }
            });
        }
        return {
            validationListeners,
            errorMessage,
            inputValue,
            inputType,
            fieldId,
            isSearchType,
            handleClear,
            showInputClear,
            isFocused,
            fieldAttrs,
            passwordToggleMode,
            showPassword,
            meta,
            maybeRequiredLabel,
        };
    },
});
